import TenantSelect from '@components/tenant-select'
// import {useAddUserMutation, useUpdateUserMutation} from '@services/users'
import {
  Drawer,
  Alert,
  Button,
  Form,
  Input,
  Space,
  Select,
  message,
  Divider,
  Table,
  Popconfirm,
  Tag,
  Tooltip
} from 'antd'
import {useCRUD, useFetchGet, useDelete, Params} from 'helpers/api'
import {isAdmin} from 'helpers/role'
import {useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {DeleteOutlined} from '@ant-design/icons'

const {Option} = Select

export const formFields = {name: 'Name', email: 'Email', role: 'Role'}

/**
 * User Editor Modal - Token Management Integration (Revised Design)
 *
 * FUNCTIONALITY: Token History Viewing and Revoke Capability ONLY (Admin Oversight)
 *
 * CONDITIONAL RENDERING MATRIX:
 * ┌─────────────────────────┬──────────────────┬────────────────────────┐
 * │ Context                 │ Show Token Panel │ Available Actions      │
 * ├─────────────────────────┼──────────────────┼────────────────────────┤
 * │ Creating New User       │ NO               │ N/A                    │
 * │ User Edits Own Profile  │ NO               │ Redirect to Profile    │
 * │ Admin Views Other User  │ YES              │ View History & Revoke  │
 * └─────────────────────────┴──────────────────┴────────────────────────┘
 *
 * Note: Full token management (create/list/revoke) is in Profile component
 */
const Editor = ({user, onClose, onSuccess}) => {
  const currentUser = useSelector(state => state.auth.user)
  const tenant = useSelector(state => state.settings.tenant)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Token viewing state (read-only for admin oversight)
  const [tokens, setTokens] = useState([])
  const [tokensLoading, setTokensLoading] = useState(false)
  const [tokenError, setTokenError] = useState(null)

  const {create, update} = useCRUD('users')

  // Optimized token API integration following DTI patterns
  const fetchTokens = useFetchGet('auth/tokens')
  const deleteToken = useDelete('auth/tokens')

  /**
   * Conditional Rendering Logic for Token Management
   *
   * User Editor Modal shows TOKEN HISTORY VIEWING AND REVOKE ONLY:
   * - isNew: True when creating a new user (no token history shown)
   * - isEditingOwnProfile: True when user edits their own profile (redirected to Profile page for full management)
   * - isAdminViewingOtherUser: True when admin edits another user (shows token history for oversight)
   * - shouldShowTokenHistory: Final condition to show token history section
   *
   * Note: Users should use the Profile page for full token management (create, list, revoke)
   *       This modal is for admin oversight only - viewing and revoking other users' tokens
   */
  const isNew = !user?.id || !user.tid
  const isEditingOwnProfile = !isNew && user?.id === currentUser?.id
  const isAdminViewingOtherUser = !isNew && user?.id !== currentUser?.id && isAdmin(currentUser)
  const shouldShowTokenHistory = isAdminViewingOtherUser && currentUser?.id && user?.id

  // const isLoading = isNew ? addStatus.isLoading : updateStatus.isLoading
  // const error = isNew ? addStatus.error : updateStatus.error
  const handleClose = () => {
    setError(null)
    setTokenError(null)
    setIsLoading(false)
    setTokensLoading(false)
    onClose()
  }

  const onFinish = values => {
    if (isNew && !user.id) {
      setIsLoading(true)
      create(values, response => {
        if (typeof response !== 'undefined') {
          setIsLoading(false)
          if (response) {
            onSuccess()
            onClose()
            handleClose()
            message.success('Successfully created user!')
          } else {
            setError({status: 400})
          }
        }
      })
    } else {
      setIsLoading(true)
      update(user.id, values, response => {
        if (typeof response !== 'undefined') {
          setIsLoading(false)
          if (response) {
            onSuccess()
            handleClose()
            message.success('Successfully updated user!')
          } else {
            setError({status: 400})
          }
        }
      })
    }
  }

  // Optimized token viewing functions with comprehensive error handling (admin oversight only)
  const loadTokens = () => {
    // Only load tokens for admin viewing other users
    if (!shouldShowTokenHistory) {
      setTokens([])
      setTokensLoading(false)
      return
    }

    setTokensLoading(true)
    setTokenError(null)

    // Use Params for query parameters following DTI patterns
    const params = Params({status: 'all'}) // Get both active and revoked tokens

    fetchTokens(params, response => {
      setTokensLoading(false)
      if (response && response.tokens) {
        setTokens(response.tokens)
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        setTokens(response)
      } else {
        setTokens([])
        // Only show error if response is explicitly falsy (connection issues)
        if (response === false || response === undefined) {
          const errorMessage = `Failed to load access tokens for ${user?.name || user?.email}. Please refresh and try again.`
          setTokenError(errorMessage)
        }
      }
    })
  }

  const handleRevokeToken = tokenId => {
    // Admin can revoke tokens for security purposes
    if (!shouldShowTokenHistory) {
      setTokenError('Access denied: Admin privileges required')
      return
    }

    setTokensLoading(true)
    setTokenError(null)
    deleteToken(
      tokenId,
      response => {
        setTokensLoading(false)
        if (response) {
          loadTokens() // Refresh the list
          message.success(
            `Access token revoked for ${user?.name || user?.email}. The user will need to create a new token.`
          )
        } else {
          const errorMessage =
            'Failed to revoke access token. The token may have already been revoked. Please try again.'
          setTokenError(errorMessage)
        }
      },
      error => {
        setTokensLoading(false)
        const errorMessage =
          error?.message ||
          error?.statusText ||
          'Failed to revoke access token. Please check your connection and try again.'
        setTokenError(errorMessage)
        console.error('Token revocation error:', error)
      }
    )
  }

  // Load tokens when component mounts or user changes
  useEffect(() => {
    if (shouldShowTokenHistory) {
      loadTokens()
    } else {
      // Clear token data when not showing token history
      setTokens([])
      setTokensLoading(false)
      setTokenError(null)
    }
  }, [user?.id, currentUser?.id, isNew, shouldShowTokenHistory])

  // Helper function to format dates with time
  const formatDateTime = dateString => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Helper function to get relative time
  const getRelativeTime = dateString => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return formatDateTime(dateString)
  }

  // Enhanced token table columns with improved display (admin view)
  const tokenColumns = [
    {
      title: 'Token Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (name, record) => (
        <div>
          <div style={{fontWeight: 500}}>{name || <em style={{color: '#999'}}>Unnamed Token</em>}</div>
          <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
            <Tag color="blue" style={{fontSize: '11px'}}>
              {record.prefix || 'N/A'}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: '20%',
      render: created => (
        <div>
          <div>{formatDateTime(created)}</div>
          <div style={{fontSize: '12px', color: '#999', marginTop: '2px'}}>{getRelativeTime(created)}</div>
        </div>
      )
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_used',
      key: 'last_used',
      width: '20%',
      render: lastUsed => (
        <div>
          <div>{lastUsed ? formatDateTime(lastUsed) : <span style={{color: '#999'}}>Never used</span>}</div>
          {lastUsed && (
            <div style={{fontSize: '12px', color: '#999', marginTop: '2px'}}>{getRelativeTime(lastUsed)}</div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'revoked',
      key: 'status',
      width: '15%',
      render: (revoked, record) => (
        <div>
          <Tag color={revoked ? 'red' : 'green'} style={{marginBottom: '4px'}}>
            {revoked ? 'Revoked' : 'Active'}
          </Tag>
          {revoked && <div style={{fontSize: '11px', color: '#999'}}>{getRelativeTime(revoked)}</div>}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      align: 'right',
      render: (_, record) => (
        <Space>
          {!record.revoked && shouldShowTokenHistory && (
            <Tooltip title="Admin action: Immediately invalidate this token for security purposes">
              <Popconfirm
                title="Revoke User Token (Admin Action)"
                description={
                  <div>
                    <p style={{marginBottom: 8}}>
                      Revoke <strong>{record.name || 'this token'}</strong> for{' '}
                      <strong>{user?.name || user?.email}</strong>?
                    </p>
                    <p style={{marginBottom: 4, color: '#ff4d4f'}}>
                      <strong>This action cannot be undone.</strong>
                    </p>
                    <p style={{marginBottom: 0, fontSize: '12px', color: '#666'}}>
                      The user's applications using this token will immediately lose API access. The user will need to
                      create a new token.
                    </p>
                  </div>
                }
                onConfirm={() => handleRevokeToken(record.id)}
                okText="Yes, Revoke Token"
                cancelText="Cancel"
                okButtonProps={{danger: true}}
                placement="topRight"
              >
                <Button type="text" danger icon={<DeleteOutlined />} size="small">
                  Revoke
                </Button>
              </Popconfirm>
            </Tooltip>
          )}
          {record.revoked && (
            <Tooltip title={`Revoked ${getRelativeTime(record.revoked)}`}>
              <span style={{color: '#999'}}>—</span>
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  return (
    <Drawer title={isNew ? 'Add User' : 'Edit User'} width={500} onClose={handleClose} open={!!user} closable={false}>
      <Form
        layout="vertical"
        initialValues={user}
        onFinish={onFinish}
        autoComplete="off"
        key={user ? user.id || -1 : 0}
      >
        {error && (
          <Alert
            message={error?.status === 400 ? 'User with this email already exists!' : 'Something went wrong!'}
            type="error"
            showIcon
            style={{marginBottom: 24}}
          />
        )}

        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: 'Name is required!'
            },
            {
              min: 2,
              message: 'Must be at least 2 characters!'
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            {
              required: true,
              message: 'Email is required!'
            },
            {
              type: 'email',
              message: 'Email is not a valid email!'
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Company"
          name="tid"
          rules={[
            {
              required: true,
              message: 'Please select a company!'
            }
          ]}
        >
          <TenantSelect disabled={tenant?.id || isAdmin(currentUser) || !isNew} />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[
            {
              required: true,
              message: 'Please select a role!'
            }
          ]}
        >
          <Select placeholder="Select a role" disabled={user?.id === currentUser.id}>
            <Option value="user">User</Option>
            <Option value="admin">Admin</Option>
            {!tenant && currentUser.role === 'sysadmin' && <Option value="sysadmin">Sysadmin</Option>}
            {((!tenant && currentUser.role === 'sysadmin') || currentUser.role === 'sysadminRO') && (
              <Option value="sysadminRO">Sysadmin (Read Only)</Option>
            )}
          </Select>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {isNew ? 'Add' : 'Save'}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Access Token History Section - Only show for admin viewing other users */}
      {shouldShowTokenHistory && (
        <>
          <Divider orientation="left">Access Token History</Divider>

          {/* Admin Notice */}
          <Alert
            message="Admin Token Oversight"
            description={
              <div>
                <p style={{marginBottom: 4}}>
                  <strong>User:</strong> {user?.name || user?.email}
                </p>
                <p style={{marginBottom: 0}}>
                  As an administrator, you can view this user's access tokens and revoke them if needed for security
                  purposes. Users can only create and manage their own tokens from their Profile page.
                </p>
              </div>
            }
            type="warning"
            showIcon
            style={{marginBottom: 16}}
          />

          {tokenError && (
            <Alert
              message={tokenError}
              type="error"
              showIcon
              style={{marginBottom: 16}}
              closable
              onClose={() => setTokenError(null)}
            />
          )}

          {/* Token History Table with responsive design */}
          <Table
            columns={tokenColumns}
            dataSource={tokens}
            rowKey="id"
            loading={tokensLoading}
            pagination={false}
            size="small"
            locale={{
              emptyText: 'This user has no access tokens'
            }}
            rowClassName={record => (record.revoked ? 'revoked-token-row' : '')}
            style={{marginBottom: 16}}
            scroll={{x: 800}}
            aria-label="User access tokens table (admin view)"
          />

          {/* Help Text */}
          <Alert
            message="Note"
            description="Users create and manage their own access tokens from their Profile page. As an administrator, you can view token activity and revoke tokens if necessary for security purposes."
            type="info"
            showIcon
            closable
          />
        </>
      )}
    </Drawer>
  )
}

export default Editor
